// Design presets for link pages. Everything is expressed as plain CSS values so
// the public page does not depend on Tailwind classes or external fonts
// (the CSP only allows self-hosted fonts, hence system font stacks).

export interface LinkItem {
  id: string;
  type: 'link' | 'header';
  label: string;
  url: string;
  icon: string;
  enabled: boolean;
  /** optional per-link button colour (#rrggbb); empty/undefined = page accent */
  color?: string;
  /** 'button' = full-width button, 'circle' = round social-style icon */
  display?: 'button' | 'circle';
}

export interface LinkPageDesign {
  theme: string;
  buttonStyle: string;
  font: string;
  layout: string;
  mode: string;
  accent: string;
  background: string;
  backgroundEnd: string;
  textColor: string;
}

export interface LinkPage {
  id?: string;
  slug: string;
  title: string;
  bio: string;
  avatar: string;
  /** optional banner image shown at the top of the card layout */
  banner?: string;
  design: LinkPageDesign;
  links: LinkItem[];
  views?: number;
  clicks?: Record<string, number>;
}

interface Palette { label: string; background: string; backgroundEnd: string; textColor: string; accent: string }

export const THEMES: Record<string, Palette | { label: string }> = {
  aurora:   { label: 'Aurora',   background: '#0f172a', backgroundEnd: '#4338ca', textColor: '#ffffff', accent: '#818cf8' },
  midnight: { label: 'Midnight', background: '#020617', backgroundEnd: '#1e293b', textColor: '#f8fafc', accent: '#38bdf8' },
  sunset:   { label: 'Sunset',   background: '#7c2d12', backgroundEnd: '#be185d', textColor: '#fff7ed', accent: '#fb923c' },
  forest:   { label: 'Forest',   background: '#052e16', backgroundEnd: '#115e59', textColor: '#ecfdf5', accent: '#34d399' },
  mono:     { label: 'Mono',     background: '#111111', backgroundEnd: '#111111', textColor: '#fafafa', accent: '#fafafa' },
  paper:    { label: 'Paper',    background: '#fafaf9', backgroundEnd: '#e7e5e4', textColor: '#1c1917', accent: '#1c1917' },
  custom:   { label: 'Eigene' },
};

const isPalette = (t: Palette | { label: string } | undefined): t is Palette => !!t && 'accent' in t;

export const BUTTON_STYLES: Record<string, string> = {
  rounded: 'Abgerundet',
  pill: 'Pille',
  square: 'Eckig',
  outline: 'Umriss',
  soft: 'Sanft',
};

export const FONTS: Record<string, { label: string; stack: string }> = {
  sans: { label: 'Sans', stack: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' },
  serif: { label: 'Serif', stack: 'Georgia, "Times New Roman", serif' },
  rounded: { label: 'Rund', stack: 'ui-rounded, "SF Pro Rounded", "Nunito", system-ui, sans-serif' },
  mono: { label: 'Mono', stack: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
};

export const ICON_OPTIONS = [
  'link', 'globe', 'mail', 'phone', 'instagram', 'linkedin', 'github', 'twitter',
  'youtube', 'facebook', 'tiktok', 'whatsapp', 'telegram', 'calendar', 'shopping',
  'music', 'file', 'map',
];

export const DEFAULT_DESIGN: LinkPageDesign = {
  theme: 'aurora', buttonStyle: 'rounded', font: 'sans', layout: 'stack', mode: 'auto',
  accent: '#6366f1', background: '#0f172a', backgroundEnd: '#312e81', textColor: '#ffffff',
};

const hexToRgb = (hex: string): [number, number, number] => {
  const h = (hex || '#000000').replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.substring(i, i + 2), 16) || 0) as [number, number, number];
};

export const rgba = (hex: string, alpha: number): string => {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Perceived brightness 0-255, used to pick a readable text colour on top of the accent.
export const luminance = (hex: string): number => {
  const [r, g, b] = hexToRgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b;
};

export function resolveDesign(design: Partial<LinkPageDesign> = {}): LinkPageDesign {
  const d = { ...DEFAULT_DESIGN, ...design };
  const preset = THEMES[d.theme];
  return isPalette(preset)
    ? { ...d, background: preset.background, backgroundEnd: preset.backgroundEnd, textColor: preset.textColor, accent: d.accent && d.theme === 'custom' ? d.accent : preset.accent }
    : d;
}

export function buildPageStyles(design: Partial<LinkPageDesign> = {}) {
  const d = resolveDesign(design);
  let { background, backgroundEnd, textColor } = d;

  // "light"/"dark" only flips presets that have an opposite counterpart
  const wantsDark = d.mode === 'dark';
  const isLightPalette = luminance(background) > 150;
  if (d.theme !== 'custom' && d.mode !== 'auto') {
    if (wantsDark && isLightPalette) {
      background = '#0b0b0f'; backgroundEnd = '#18181b'; textColor = '#fafafa';
    } else if (!wantsDark && !isLightPalette) {
      background = '#fafaf9'; backgroundEnd = '#e7e5e4'; textColor = '#1c1917';
    }
  }

  const accentText = luminance(d.accent) > 160 ? '#111111' : '#ffffff';

  // "card" layout: content sits on a rounded surface (like the contact cards)
  // with a banner strip on top, so we need surface colours derived from the
  // resolved page palette.
  const isCard = d.layout === 'card';
  const pageIsLight = luminance(background) > 150;
  const surface = pageIsLight ? '#ffffff' : '#17171d';
  const surfaceText = pageIsLight ? '#1c1917' : '#fafafa';
  const banner = `linear-gradient(150deg, ${d.accent} 0%, ${backgroundEnd} 140%)`;

  let radius = ({ rounded: 14, pill: 999, square: 4, outline: 14, soft: 18 } as Record<string, number>)[d.buttonStyle] ?? 14;
  // full pills look odd on the tall grid tiles
  if (d.layout === 'grid') radius = Math.min(radius, 28);

  const button: Record<string, string | number> = { borderRadius: radius };
  // on the card surface, "outline"/"soft" text must contrast with the surface, not the page
  const buttonText = isCard ? surfaceText : textColor;
  if (d.buttonStyle === 'outline') {
    button['background'] = 'transparent';
    button['color'] = buttonText;
    button['border'] = `2px solid ${buttonText}`;
    button['boxShadow'] = 'none';
  } else if (d.buttonStyle === 'soft') {
    button['background'] = rgba(d.accent, 0.22);
    button['color'] = buttonText;
    button['border'] = `1px solid ${rgba(d.accent, 0.35)}`;
    button['boxShadow'] = 'none';
  } else {
    button['background'] = d.accent;
    button['color'] = accentText;
    button['border'] = '1px solid transparent';
    button['boxShadow'] = `0 6px 18px ${rgba(d.accent, 0.35)}`;
  }

  return {
    page: {
      minHeight: '100%',
      background: `linear-gradient(160deg, ${background} 0%, ${backgroundEnd} 100%)`,
      color: textColor,
      fontFamily: (FONTS[d.font] ?? FONTS['sans']!).stack,
    },
    button,
    muted: rgba(isCard ? surfaceText : textColor, 0.72),
    avatarRing: rgba(isCard ? surfaceText : textColor, 0.35),
    headerLine: rgba(isCard ? surfaceText : textColor, 0.25),
    card: { surface, surfaceText, surfaceMuted: rgba(surfaceText, 0.66), banner },
    resolved: { ...d, background, backgroundEnd, textColor },
  };
}

/**
 * Button style for a single link with its own colour override. Builds on the
 * fully resolved design so light/dark flips and presets stay intact.
 */
export function buildLinkButton(resolvedDesign: LinkPageDesign, color: string): Record<string, string | number> {
  return buildPageStyles({ ...resolvedDesign, theme: 'custom', accent: color }).button;
}
